import ViewModel from '@/ViewModels/ViewModel';
import type ControlFactory from '@/Factories/ControlFactory';
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import { IControlViewModel } from '#/CoreFrameInterfaces';
import type LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import BusyCounter from '@/Utility/BusyCounter';

export default class ControlViewModel extends ViewModel implements IControlViewModel {

    /**
     * @inheritdoc 
     */
    public isDisposable: boolean;

    /**
     * @inheritdoc 
     */
    public factory: ControlFactory;

    /**
     * @inheritdoc 
     */
    public isSummaryViewMode: boolean;

    /**
     * @inheritdoc 
     */
    public isBusy: KnockoutObservable<boolean>;
    private busyCounter: BusyCounter;

    /**
    * @inheritdoc
    */
    public isDisplayed: KnockoutObservable<boolean>;

    /**
     * @inheritdoc 
     */
    public isVisible: KnockoutObservable<boolean>;

    /**
     * @inheritdoc 
     */
    public isEnabled: KnockoutObservable<boolean>;

    /**
     * @inheritdoc 
     */
    public isRequired: KnockoutObservable<boolean>;

    /**
     * @inheritdoc 
     */
    public isFocusable: KnockoutObservable<boolean>;

    /**
     * @inheritdoc 
     */
    public invalidationText: KnockoutObservable<string>;

    /**
     * @inheritdoc 
     */
    public isValid: KnockoutObservable<boolean>;

    /**
     * @inheritdoc 
     */
    public animation: KnockoutObservable<boolean | kendo.ui.WindowAnimation>;

    /**
     * @inheritdoc 
     */
    public isActive: KnockoutObservable<boolean>;

    /**
     * @inheritdoc 
     */
    public tooltip: LabelViewModel;

    /**
     * @inheritdoc 
     */
    public technicalName: string;

    public constructor(protected params: CoreFrameInterfaces.IControlViewModelConstructorParameter) {
        super(params);

        this.isDisposable = this.paramOrDefault(true);
        this.factory = this.paramOrDefault(params.factory, null);
        this.isSummaryViewMode = this.paramOrDefault(params.isSummaryViewMode, false);
        this.tooltip = this.paramOrDefault(params.tooltip, null);
        this.technicalName = this.paramOrDefault(params.technicalName, null);

        this.isBusy = this.observableOrDefault(params.isBusy, false);
        this.isDisplayed = this.observableOrDefault(params.isDisplayed, true);
        this.isRequired = this.observableOrDefault(params.isRequired, false);
        this.isFocusable = this.observableOrDefault(params.isFocusable, false);
        this.invalidationText = this.observableOrDefault(params.invalidationText, '');
        this.isValid = this.observableOrDefault(params.isValid, true);
        this.animation = this.observableOrDefault(false);
        this.isActive = this.observableOrDefault(params.isActive, false);

        this.undisposeProperties.push('context');

        this.isVisible = this.createIsVisibleObservable();
        this.isEnabled = this.createIsEnabledObservable();
    }

    /**
     * Erzeugt das Observable für "isEnabled"
     */
    protected createIsEnabledObservable(): KnockoutObservable<boolean> {
        return this.observableOrDefault(this.params.isEnabled, true);
    }

    /**
     * Erzeugt das Observable für "isVisible"
     */
    protected createIsVisibleObservable(): KnockoutObservable<boolean> {
        return this.observableOrDefault(this.params.isVisible, true);
    }

    /**
     * Setzt den Focusable-State gleich dem Enabled-State 
     */
    public setFocusableToEnabledState(): void {
        this.isFocusable(this.isEnabled());
    }

    /**
     * Synchronisiert den Focusable-State (Slave) mit dem Enabled-State (Master).
     */
    public syncFocusableWithEnabledState(): void {
        this.defineSubscription(this.isEnabled, (isEnabled) => {
            this.setFocusableToEnabledState();
        })(this.isEnabled());
    }

    /**
     * Testet, ob alle in den Parametern übergebenen Properties im ViewModel vorhanden sind
     * Dient der frühzeitigen Erkennung von Programmierfehlern
     */
    public static checkProperties(instance: ControlViewModel, params: CoreFrameInterfaces.IControlViewModelConstructorParameter): void {
        const instanceKeys = Object.keys(instance);
        for (const key of Object.keys(params)) {
            if (!instanceKeys.includes(key)) {
                throw 'Invalid Parameter Error: Der Parameter mit dem Namen "' + key + '" ist nicht als Property im ViewModel "' + instance.type + '" enthalten';
            }
        }
    }

    /**
     * Setzt den Wert / die Werte des ViewModels zurück
     */
    public resetValue(): void {
        // wird in den Unterklassen implementiert
    }

    public async executeBusilyAsync<T>(action: () => T | Promise<T>): Promise<T> {
        if (this.isDisposed)
            return undefined;

        this.increaseBusyCounter();
        try {
            return await action();
        } finally {
            this.decreaseBusyCounter();
        }
    }

    public increaseBusyCounter(): void {
        if (this.isDisposed)
            return;

        if (!this.busyCounter)
            this.busyCounter = new BusyCounter(this.isBusy);

        this.busyCounter.increment();
    }

    public decreaseBusyCounter(): void {
        if (this.isDisposed || !this.busyCounter)
            return;

        this.busyCounter.decrement();
    }
}